import React from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { NotificationsProvider } from "./context/NotificationsContext";
import { SocketProvider } from "./context/SocketContext";
import { ThemeProvider } from "./context/ThemeContext";
import { MobileMenuProvider } from "./context/MobileMenuContext";
import { UnsavedChangesProvider } from "./context/UnsavedChangesContext";
import {
  AssistantConversationProvider,
  AssistantProvider,
} from "./context/AssistantContext";
import { VersionProvider } from "./context/VersionContext";
import { PushProvider } from "./context/PushContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import { LoginPage } from "./components/auth/LoginPage";
import { SelectRolePage } from "./components/auth/SelectRolePage";
import { OnboardingPage } from "./components/auth/OnboardingPage";
import { AccessRequestPage } from "./components/auth/AccessRequestPage";
import { OnboardingProviderPage } from "./components/auth/OnboardingProviderPage";
import { AdminLoginPage } from "./components/admin/AdminLoginPage";
import { AdminDashboardPage } from "./components/admin/AdminDashboardPage";
import { AdminImpersonatePage } from "./components/admin/AdminImpersonatePage";
import { DashboardLayout } from "./components/dashboard/DashboardLayout";
import { Toaster } from "./components/ui/toaster";
import { UpdateBanner } from "./components/ui/UpdateBanner";

import { ProviderOnly } from "./components/invoices/ProviderOnly";
const HomePage = React.lazy(() => import("./components/dashboard/HomePage").then(m => ({ default: m.HomePage })));
const ArchivedOrdersPage = React.lazy(() => import("./components/archive/ArchivedOrdersPage").then(m => ({ default: m.ArchivedOrdersPage })));
const InvoicesListPage = React.lazy(() => import("./components/invoices/InvoicesListPage").then(m => ({ default: m.InvoicesListPage })));
const InvoiceFormPage = React.lazy(() => import("./components/invoices/InvoiceFormPage").then(m => ({ default: m.InvoiceFormPage })));
const SellerDetailsPage = React.lazy(() => import("./components/invoices/SellerDetailsPage").then(m => ({ default: m.SellerDetailsPage })));
const PartnersPage = React.lazy(() => import("./components/partners/PartnersPage").then(m => ({ default: m.PartnersPage })));
const ContractDetailPage = React.lazy(() => import("./components/partners/ContractDetailPage").then(m => ({ default: m.ContractDetailPage })));
const PartnerCategoryPage = React.lazy(() => import("./components/partners/PartnerCategoryPage").then(m => ({ default: m.PartnerCategoryPage })));
const ContractCreatePage = React.lazy(() => import("./components/partners/ContractCreatePage").then(m => ({ default: m.ContractCreatePage })));
const LegalPage = React.lazy(() => import("./components/legal/LegalPage"));
const ServicesPage = React.lazy(() => import("./components/services/ServicesPage").then(m => ({ default: m.ServicesPage })));
const OrderDetailPage = React.lazy(() => import("./components/services/OrderDetailPage").then(m => ({ default: m.OrderDetailPage })));
const NotificationsPage = React.lazy(() => import("./components/notifications/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const HouseInfoPage = React.lazy(() => import("./components/house-info/HouseInfoPage").then(m => ({ default: m.HouseInfoPage })));
const CompanyInfoPage = React.lazy(() => import("./components/company-info/CompanyInfoPage").then(m => ({ default: m.CompanyInfoPage })));
const NewsPage = React.lazy(() => import("./components/news/NewsPage").then(m => ({ default: m.NewsPage })));
const SettingsPage = React.lazy(() => import("./components/settings/SettingsPage").then(m => ({ default: m.SettingsPage })));
const HelpPage = React.lazy(() => import("./components/help/HelpPage").then(m => ({ default: m.HelpPage })));
const GettingStartedPage = React.lazy(() => import("./components/help/GettingStartedPage").then(m => ({ default: m.GettingStartedPage })));
const ServiceOrderTutorial = React.lazy(() => import("./components/help/ServiceOrderTutorial").then(m => ({ default: m.ServiceOrderTutorial })));
const SalesPage = React.lazy(() => import("./components/sales/SalesPage").then(m => ({ default: m.SalesPage })));
const ConfluencePage = React.lazy(() => import("./components/confluence/ConfluencePage").then(m => ({ default: m.ConfluencePage })));
const LegacyConfluenceRedirect = React.lazy(() => import("./components/development/LegacyConfluenceRedirect").then(m => ({ default: m.LegacyConfluenceRedirect })));
const DevelopmentPage = React.lazy(() => import("./components/development/DevelopmentPage").then(m => ({ default: m.DevelopmentPage })));
const LandingPage = React.lazy(() => import("./components/landing/LandingPage").then(m => ({ default: m.LandingPage })));

/**
 * Everything that wraps the routes. It lives inside the router as the root
 * route's element so the providers can use routing hooks — UnsavedChangesProvider
 * needs `useBlocker`, which only a data router has (BUG-16).
 */
function AppShell() {
  return (
    <UnsavedChangesProvider>
      <AuthProvider>
        <NotificationsProvider>
          <SocketProvider>
            <VersionProvider>
              <PushProvider>
              {/* The assistant's context and conversation sit ABOVE the Suspense
                  boundary below: a page publishes its open object into the first,
                  and the second must outlive any remount of the routed subtree
                  (ticket 21, §D1/§D14). The widget itself is mounted in
                  DashboardLayout, so no public page renders it. */}
              <AssistantProvider>
              <AssistantConversationProvider>
                {/* Update notification banner */}
                <UpdateBanner />

                {/* Toast notifications */}
                <Toaster position="top-right" expand closeButton />

                <ErrorBoundary>
                  <React.Suspense fallback={
                    <div className="min-h-screen flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  }>
                    <Outlet />
                  </React.Suspense>
                </ErrorBoundary>
              </AssistantConversationProvider>
              </AssistantProvider>
              </PushProvider>
            </VersionProvider>
          </SocketProvider>
        </NotificationsProvider>
      </AuthProvider>
    </UnsavedChangesProvider>
  );
}

// Built once, at module scope: a router rebuilt on every render would remount
// the whole tree and lose every open form.
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppShell />}>
      {/* Public routes */}
      {/* Promo landing on the root; /promo was its preview address (ticket 8) */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/promo" element={<Navigate to="/" replace />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/select-role" element={<SelectRolePage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/access-request" element={<AccessRequestPage />} />
      <Route path="/onboarding-provider" element={<OnboardingProviderPage />} />
      {/* Internal task & bug board — its own login, outside the product session */}
      <Route path="/development" element={<DevelopmentPage />} />
      {/* The board owns the documentation since BUG-4 */}
      <Route path="/development/confluence" element={<ConfluencePage />} />
      {/* The docs' old cabinet address — bookmarks keep working */}
      <Route path="/confluence" element={<LegacyConfluenceRedirect />} />
      <Route path="/admin" element={<AdminLoginPage />} />
      {/* Sellers' workspace: own login, own cookie, outside the product layout (ticket 7) */}
      <Route path="/sales" element={<SalesPage />} />
      <Route
        path="/admin/dashboard"
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/impersonate"
        element={
          <AdminRoute>
            <AdminImpersonatePage />
          </AdminRoute>
        }
      />

      {/* Protected routes: a pathless layout, the cabinet home lives at /dashboard */}
      <Route
        element={
          <ProtectedRoute>
            <MobileMenuProvider>
              <DashboardLayout />
            </MobileMenuProvider>
          </ProtectedRoute>
        }>
        <Route path="dashboard" element={<HomePage />} />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="partners/new" element={<ContractCreatePage />} />
        <Route path="partners/category/:key" element={<PartnerCategoryPage />} />
        <Route path="partners/:contractId" element={<ContractDetailPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="services/order/:orderId" element={<OrderDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="house-info" element={<HouseInfoPage />} />
        <Route path="company-info" element={<CompanyInfoPage />} />
        <Route path="news" element={<NewsPage />} />
        <Route path="archive" element={<ArchivedOrdersPage />} />
        {/* Ticket 25: provider-only; a building gets the section's own 404. */}
        <Route path="extra-services/invoices" element={<ProviderOnly><InvoicesListPage /></ProviderOnly>} />
        <Route path="extra-services/invoices/new" element={<ProviderOnly><InvoiceFormPage /></ProviderOnly>} />
        <Route path="extra-services/invoices/my-data" element={<ProviderOnly><SellerDetailsPage /></ProviderOnly>} />
        <Route path="extra-services/invoices/:id" element={<ProviderOnly><InvoiceFormPage /></ProviderOnly>} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpPage />}>
          <Route path="getting-started" element={<GettingStartedPage />}>
            <Route path="service-orders" element={<ServiceOrderTutorial />} />
          </Route>
        </Route>
      </Route>

      {/* Fallback: the cabinet home (ProtectedRoute sends a guest on to /login) */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Route>
  )
);

function App() {
  // Dropping a file outside an upload zone must not navigate the browser
  // to the file — swallow the default behavior app-wide.
  React.useEffect(() => {
    const preventDefault = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);
    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}

export default App;
